import * as React from 'react';
import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import { AuthContext } from "../context/AuthContext";
import { CloudContext } from "../context/CloudContext";
import { getActivities } from "../utils/activityManagement";
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SvgIcon from '@mui/material/SvgIcon';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClockIcon from '@heroicons/react/24/solid/ClockIcon';
import SettingsIcon from '@mui/icons-material/Settings';
import CircleStackIcon from '@heroicons/react/24/solid/CircleStackIcon';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LoginIcon from '@mui/icons-material/Login';
import Tooltip from '@mui/material/Tooltip';
import { formatSizeUnits } from '../utils/fileManagement';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { PieChart, pieClasses } from '@mui/x-charts/PieChart';
import { formatSizeToGb } from '../utils/fileManagement';
import { LineChart } from '@mui/x-charts/LineChart';
import { lineClasses } from '@mui/x-charts/LineChart';
import UserSettingDialog from "../dialog/UserSettingDialog.jsx";
import ProcessingHistoryDialog from "../dialog/ProcessingHistoryDialog.jsx";
import ProjectList from './ProjectList';
import { useTranslation } from "react-i18next";
import { getStorageData } from "../utils/interfaceS3";
import config from '../config.js';

const palette = ['#2072AF', '#7BAFD4', '#4bc098', '#FF8042'];

function UserDashboard({ userSelected, reloadUsers, processings }) {
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const { jwt, idToken, service } = useContext(AuthContext);
  const { region, idpool } = useContext(CloudContext);
  const { t } = useTranslation();

  // State for chart data
  const [activityData, setActivityData] = useState([]);
  const [sizeUpload, setSizeUpload] = useState(0);
  const [sizeView, setSizeView] = useState(0);
  const [sizeProduct, setSizeProduct] = useState(0);
  const [sizeTotal, setSizeTotal] = useState(0);
  const [openDialogUserSetting, setOpenDialogUserSetting] = useState(false);
  const [openDialogProcessingHistory, setOpenDialogProcessingHistory] = useState(false);

  // Callback for opening user settings
  const handleSettingUserClick = useCallback(() => {
    setOpenDialogUserSetting(true);
  }, []);

    // Callback for opening user settings
    const handleProcessingHistoryUserClick = useCallback(() => {
      setOpenDialogProcessingHistory(true);
    }, []);

  // Format date and time once
  const getFormattedDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  }, []);

  const getFormattedTime = useCallback((dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString();
  }, []);

  // Fetch chart data (activities and storage sizes)
  const displayChart = useCallback(() => {
    if (userSelected) {
      getActivities(userSelected.id, jwt).then((res) => {
        const filterActivity = res.filter(r=>r.attributes.service===service);
        setActivityData(filterActivity);
      });
      getStorageData(userSelected.username, idToken, region, idpool).then((res) => {
        setSizeUpload(res.sizeUpload);
        setSizeView(res.sizeView);
        setSizeProduct(res.sizeProduct);
        setSizeTotal(res.sizeUpload + res.sizeView + res.sizeProduct);
      });
    }
  }, [userSelected, jwt, idToken, region, idpool,service]);

  useEffect(() => {
    displayChart();
  }, [displayChart]);

  // Group activity counts per month
  const groupedData = useMemo(() => {
    return activityData.reduce((acc, activity) => {
      const monthYear = new Date(activity.attributes.createdAt).toLocaleString('default', { month: 'long', year: 'numeric' });
      acc[monthYear] = (acc[monthYear] || 0) + 1;
      return acc;
    }, {});
  }, [activityData]);

  // Group file sizes per month (assuming activity.attributes.size is in octets)
  const groupedSize = useMemo(() => {
    return activityData.reduce((acc, activity) => {
      const monthYear = new Date(activity.attributes.createdAt).toLocaleString('default', { month: 'long', year: 'numeric' });
      acc[monthYear] = (acc[monthYear] || 0) + Number(activity.attributes.size);
      return acc;
    }, {});
  }, [activityData]);

  // Generate labels and data for the past 12 months
  const { labels, data, dataSizePerMonth } = useMemo(() => {
    const labels = [];
    const data = [];
    const dataSizePerMonth = [];
    const currentDate = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const lab = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      labels.push(lab);
      data.push(groupedData[lab] || 0);
      const sizeOctets = groupedSize[lab] || 0;
      dataSizePerMonth.push(formatSizeToGb(sizeOctets));
    }
    return { labels, data, dataSizePerMonth };
  }, [groupedData, groupedSize]);

  // Prepare data for the doughnut (pie) chart
  const dataDoughnut = useMemo(() => {
    const arr = [];
    if (sizeUpload > 1000000) {
      arr.push({ label: t("Upload"), value: formatSizeToGb(sizeUpload) });
    }
    if (sizeView > 1000000) {
      arr.push({ label: t("View"), value: formatSizeToGb(sizeView) });
    }
    if (sizeProduct > 1000000) {
      arr.push({ label: t("Products"), value: formatSizeToGb(sizeProduct) });
    }
    return arr;
  }, [sizeUpload, sizeView, sizeProduct, t]);

  const TOTAL = useMemo(() => {
    return dataDoughnut.reduce((acc, item) => acc + item.value, 0);
  }, [dataDoughnut]);

  const getArcLabel = useCallback(
    (params) => {
      const percent = params.value / TOTAL;
      return `${(percent * 100).toFixed(0)}%`;
    },
    [TOTAL]
  );

  return (
    <div>
      <Stack spacing={3}>
        <Box sx={{ paddingX: 3, paddingY: 1 }}>
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              '&:hover': {
                boxShadow: '0px 5px 22px rgba(0, 0, 0, 0.06), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.04)',
                transform: 'none',
              },
            }}
          >
            <CardHeader
              sx={{
                display: 'flex',
                overflow: 'hidden',
                '& .MuiCardHeader-content': { overflow: 'hidden' },
              }}
              action={
                <>
                <Tooltip title={t("HistoryRequests")} placement="bottom">
                <IconButton aria-label="settings" onClick={handleProcessingHistoryUserClick}>
                  <FormatListBulletedIcon />
                </IconButton>
                </Tooltip>
                <Tooltip title={t("Settings")} placement="bottom">
                <IconButton aria-label="settings" onClick={handleSettingUserClick}>
                  <SettingsIcon />
                </IconButton>
                </Tooltip>
                </>
              }
              title={
                <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
                  {userSelected && (
                    <>
                      {userSelected.admin && <AdminPanelSettingsIcon />}
                      <Typography variant="h5">{userSelected.email}</Typography>
                    </>
                  )}
                </Stack>
              }
              subheader={
                <Box sx={{ paddingY: 1 }}>
                  <Chip label={userSelected?.username} sx={{ color: 'neutral.500' }} />
                </Box>
              }
            />
            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
              {/* Show PieChart only for classic service */}
              {service === "classic" && (
                <Grid size={{ xs: 4, sm: 8, md: 4 }}>
                  <Box
                    height={300}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ paddingX: 2, overflow: 'hidden', minWidth: 0 }}
                  >
                    <PieChart
                      colors={palette}
                      series={[
                        {
                          arcLabel: getArcLabel,
                          data: dataDoughnut,
                          innerRadius: 30,
                          outerRadius: 100,
                          paddingAngle: 5,
                          cornerRadius: 5,
                          startAngle: 0,
                          endAngle: 360,
                        },
                      ]}
                      sx={{
                        [`& .${pieClasses.arcLabel}`]: {
                          fill: 'white',
                          fontSize: 14,
                        },
                      }}
                      height={300}
                      margin={{
                        left: 5,
                        right: 5,
                        top: 5,
                        bottom: 5,
                      }}
                    />
                  </Box>
                </Grid>
              )}
              {/* LineChart takes full width for mapping service, or partial width for classic service */}
              <Grid size={service === "mapping" ? { xs: 4, sm: 8, md: 12 } : { xs: 4, sm: 8, md: 8 }}>
                <Box
                  height={300}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ paddingX: 2, overflow: 'hidden', width: '100%', minWidth: 0 }}
                >
                  <LineChart
                    height={280}
                    series={[
                      { data: data, label: t("NumberFile"), yAxisId: 'leftAxisId' },
                      { data: dataSizePerMonth, label: t("NumberGo"), yAxisId: 'rightAxisId' },
                    ]}
                    xAxis={[{ scaleType: 'point', data: labels }]}
                    yAxis={[{ id: 'leftAxisId' }, { id: 'rightAxisId' }]}
                    margin={{
                      left: 60,
                      right: 60,
                      top: 20,
                      bottom: 40,
                    }}
                    legend={{
                      direction: 'row',
                      position: { vertical: 'top', horizontal: 'middle' }
                    }}
                    sx={{
                      width: '100%',
                      [`& .${lineClasses.mark}`]: {
                        display: 'none',
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
            <Divider />
            <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <SvgIcon color="action" fontSize="small">
                  <ClockIcon />
                </SvgIcon>
                {userSelected && (
                  <Typography color="text.secondary" display="inline" variant="body2" sx={{ pr: 2 }}>
                    {lgUp && t("UserCreated")}
                    {getFormattedDate(userSelected.createdAt)}
                  </Typography>
                )}
                {userSelected && userSelected.lastLogin && (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <SvgIcon color="action" fontSize="small">
                      <LoginIcon />
                    </SvgIcon>
                    <Typography color="text.secondary" display="inline" variant="body2">
                      {lgUp && t("LastLogin")}
                      {getFormattedDate(userSelected.lastLogin)} at {getFormattedTime(userSelected.lastLogin)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
              {service === config.services.classic && <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <SvgIcon color="action" fontSize="small">
                  <CloudUploadIcon />
                </SvgIcon>
                <Typography color="text.secondary" display="inline" variant="body2" sx={{ pr: 2 }}>
                  {lgUp && t("Upload")}
                  {formatSizeUnits(sizeUpload)}
                </Typography>
                <SvgIcon color="action" fontSize="small">
                  <CircleStackIcon />
                </SvgIcon>
                <Typography color="text.secondary" display="inline" variant="body2">
                  {lgUp && t("Total")}
                  {formatSizeUnits(sizeTotal)}
                </Typography>
              </Stack>}
            </Stack>
          </Card>
        </Box>
        <Box sx={{ paddingX: 0 }}>
          {userSelected && <ProjectList projects={userSelected.projects.filter(proj=>proj.service===service)} userid={userSelected.username} processings={processings}/>}
        </Box>
      </Stack>
      {userSelected && (
        <UserSettingDialog
          open={openDialogUserSetting}
          setOpen={setOpenDialogUserSetting}
          user={userSelected}
          reloadUsers={reloadUsers}
        />
      )}
      {userSelected && (
        <ProcessingHistoryDialog
          open={openDialogProcessingHistory}
          setOpen={setOpenDialogProcessingHistory}
          user={userSelected}
        />
      )}
    </div>
  );
}

export default React.memo(UserDashboard);
